import { z } from 'zod'
import type { CardTexture, ClientTheme } from './branding'
import { displayNoticeMaxLength } from './display-clock-extras'
import type { DisplayLayout } from './display-layout'
import type { DisplayConfigFile, DisplayStickFile } from './display-stick'
import type { GameClockOutlook, GameClockView } from './game-clock'
import type { GameStatus } from './index'

export const displayGrantKinds = ['stick', 'browser'] as const
export type DisplayGrantKind = (typeof displayGrantKinds)[number]
export type DisplayAccessLevel = 'full' | 'legacy'

export const displayCookieName = 'feltos_display_key' as const
export const displayCookieMaxAgeSeconds = 60 * 60 * 24 * 365 * 10
export const displayBrowserCookieMaxAgeSeconds = 60 * 60 * 24 * 365

const displayTableIdField = {
  tableId: z.string().uuid('请选择桌台').optional(),
}

export const displayOpenGameSchema = z.discriminatedUnion('mode', [
  z.object({
    mode: z.literal('join'),
    gameId: z.string().uuid('请选择赛事'),
    dealerStaffId: z.string().uuid('请选择裁判'),
    ...displayTableIdField,
  }),
  z.object({
    mode: z.literal('create'),
    gameTemplateId: z.string().uuid('请选择赛制'),
    dealerStaffId: z.string().uuid('请选择裁判'),
    ...displayTableIdField,
  }),
])

export type DisplayOpenGameInput = z.infer<typeof displayOpenGameSchema>

/** 关桌、调时钟：不传则默认主桌，兼容还没刷新的老客户端 */
export const displayTableTargetSchema = z.object({
  tableId: z.string().uuid('请选择桌台').optional(),
})

export type DisplayTableTarget = z.infer<typeof displayTableTargetSchema>

export const tableDisplayPatchSchema = z
  .object({
    allowsLegacyDisplay: z.boolean().optional(),
    subTableIds: z
      .array(z.string().uuid('桌台不合法'))
      .max(20, '副桌过多')
      .optional(),
    notice: z
      .string()
      .max(displayNoticeMaxLength, `公告不能超过 ${displayNoticeMaxLength} 字`)
      .transform((value) => value.replace(/\r\n?/g, '\n').trimEnd())
      .optional(),
  })
  .refine(
    (value) =>
      value.allowsLegacyDisplay !== undefined ||
      value.subTableIds !== undefined ||
      value.notice !== undefined,
    { message: '没有要更新的内容' },
  )

export type TableDisplayPatch = z.infer<typeof tableDisplayPatchSchema>

export const tableDisplaySubTablesSchema = z.object({
  subTableIds: z.array(z.string().uuid('桌台不合法')).max(20, '副桌过多'),
})

export type TableDisplaySubTables = z.infer<typeof tableDisplaySubTablesSchema>

/** 一块屏最多同时显示两场赛事 */
export const displayMaxPanes = 2

export interface DisplayPaneSourceTable {
  id: string
  name: string
}

export interface DisplayPaneSourceLive {
  tableId: string
  gameId: string
}

export interface DisplayPaneGroup {
  gameId: string
  tableIds: string[]
  tableNames: string[]
}

/**
 * 按赛事分组：绑定桌顺序是主桌在前、副桌按桌名。
 * 同一场赛事合成一个分屏区；顺序取该场第一张出现的桌。超过上限的场次丢掉。
 */
export function buildDisplayPanes(
  boundTables: DisplayPaneSourceTable[],
  live: DisplayPaneSourceLive[],
  maxPanes = displayMaxPanes,
): DisplayPaneGroup[] {
  const gameByTable = new Map<string, string>()
  for (const row of live) gameByTable.set(row.tableId, row.gameId)

  const groups: DisplayPaneGroup[] = []
  const indexByGame = new Map<string, number>()
  for (const table of boundTables) {
    const gameId = gameByTable.get(table.id)
    if (!gameId) continue
    const existing = indexByGame.get(gameId)
    if (existing !== undefined) {
      groups[existing]!.tableIds.push(table.id)
      groups[existing]!.tableNames.push(table.name)
      continue
    }
    indexByGame.set(gameId, groups.length)
    groups.push({
      gameId,
      tableIds: [table.id],
      tableNames: [table.name],
    })
  }
  return groups.slice(0, maxPanes)
}

export const displayAppSessionSchema = z.object({
  key: z.string().min(1, '缺少凭证'),
  appVersion: z.string().trim().max(32).optional(),
})

export type DisplayAppSessionInput = z.infer<typeof displayAppSessionSchema>

/** 登录后只允许跳回本站大屏页，防止被用来跳到外部网站 */
export function safeDisplayNext(value: string | null | undefined): string | null {
  if (!value) return null
  if (value.charAt(0) !== '/') return null
  if (value.indexOf('//') >= 0 || value.indexOf('\\') >= 0) return null
  if (value.indexOf('\n') >= 0 || value.indexOf('\r') >= 0) return null
  if (value.indexOf('\t') >= 0) return null
  try {
    const url = new URL(value, 'https://ifelt.cn')
    if (url.origin !== 'https://ifelt.cn') return null
    if (url.pathname === '/display' || url.pathname.indexOf('/display/') !== 0)
      return null
    return url.pathname + url.search
  } catch {
    return null
  }
}

export interface DisplayTableView {
  name: string
  storeName: string
}

/**
 * 全场汇总，口径与裁判端一致：
 * 总计分是整场计分事件净和（含已出局玩家买入，牌桌内部转移不记账），
 * 人均按未出局人数摊。
 */
export interface DisplaySummary {
  playerCount: number
  activePlayerCount: number
  totalChips: number
  averageChips: number
}

/** 级别播报的音频地址。休息、语音未配置时为 null；素材缺失时播放失败，大屏只响提示音 */
export interface DisplaySpeech {
  currentUrl: string | null
  /** 下一个计分级别的播报，大屏提前缓冲，跨级时不等网络 */
  nextUrl: string | null
}

/** 大屏花名册：未出局玩家的现场可见字段，不含座位、码量和身份 */
export interface DisplayRosterEntry {
  nickname: string
  avatarUrl: string | null
  /** 本场已交分的入场加续场手数 */
  signupCount: number
  isWaiting: boolean
}

export interface DisplayGameView {
  name: string
  templateName: string
  startedAt: string
  isPreparing: boolean
  /** 当前可带入的计分，取这场冻结的入场计分 */
  entryChips: number
  /** 入场计分折成后位基础分数；没有后位基础分时为 null */
  entryBb: number | null
  summary: DisplaySummary
  roster: DisplayRosterEntry[]
  /** 本场没有级别提升时钟时，时钟与展望都为 null */
  clock: GameClockView | null
  outlook: GameClockOutlook | null
  speech: DisplaySpeech | null
}

export interface DisplayPane {
  tableIds: string[]
  tableNames: string[]
  game: DisplayGameView
}

export interface DisplayTableSlot {
  id: string
  name: string
  isOccupied: boolean
  canCloseTable: boolean
  closeTableBlockReason: string | null
}

export interface DisplayCandidateTable {
  id: string
  name: string
  isSelected: boolean
  blockReason: string | null
}

interface DisplaySnapshotBase {
  serverNow: string
  table: DisplayTableView
  /** 租户大屏布局；库里没有或已损坏时是默认布局 */
  layout: DisplayLayout
  backgroundUrl: string | null
  /** 租户标志，时钟合成顶栏用；没有就不画 */
  logoUrl: string | null
  /** 本桌右下角公告，只有 HiPro 蓝皮肤时钟有；清空后为 null */
  notice: string | null
  /** 持有完整凭证且至少一张绑定桌空闲时才能开桌 */
  canOpenTable: boolean
  openTableBlockReason: string | null
  /** 持有完整凭证且至少一张绑定桌满足收桌条件时才能关桌 */
  canCloseTable: boolean
  closeTableBlockReason: string | null
  /** 本屏还能再新开一场；已有两场时只能加入 */
  canCreateGame: boolean
  createGameBlockReason: string | null
  /** 按赛事分组的分屏区，最多两区；空表示全屏空闲 */
  panes: DisplayPane[]
  /** 主桌在前、副桌按桌名。开桌关桌选桌用 */
  tables: DisplayTableSlot[]
}

export interface DisplayIdleSnapshot extends DisplaySnapshotBase {
  kind: 'idle'
}

export interface DisplayLiveSnapshot extends DisplaySnapshotBase {
  kind: 'live'
  game: DisplayGameView
}

/** 投屏令牌无效由接口回 404，不占一个状态 */
export type DisplaySnapshot = DisplayIdleSnapshot | DisplayLiveSnapshot

export interface DisplayPodiumEntry {
  rank: number
  rankLabel: string
  nickname: string
  avatarUrl: string | null
  /** 这一名实际发到的奖励，没有则不展示 */
  prizeText: string | null
}

/** 本场领奖台。entries 从左到右是亚军、冠军、季军，缺的名次不出现 */
export interface DisplayPodium {
  gameName: string
  brandName: string
  logoUrl: string | null
  clientTheme: ClientTheme
  cardTexture: CardTexture
  entries: DisplayPodiumEntry[]
}

/** 大屏关桌结果。合桌、常规赛、结束时没有未出局会员时 podium 为 null */
export interface DisplayCloseResult {
  podium: DisplayPodium | null
}

function podiumRankLabel(rank: number) {
  if (rank === 1) return '冠军'
  if (rank === 2) return '亚军'
  return '季军'
}

/**
 * 整场锦标赛结束、而且还有未出局会员时，取前三名。
 * 从左到右按亚军、冠军、季军排，方便摆成领奖台。
 */
export function displayPodiumOf(input: {
  isGameFinished: boolean
  kind: 'tournament' | 'normal'
  /** 结束时仍未出局的会员数 */
  presentCount: number
  rankings: Array<{
    rank: number
    nickname: string
    avatarUrl: string | null
    prizeText?: string | null
  }>
}): DisplayPodiumEntry[] | null {
  if (!input.isGameFinished || input.kind !== 'tournament') return null
  if (input.presentCount < 1) return null
  const picked: Array<DisplayPodiumEntry | undefined> = []
  for (const row of input.rankings) {
    if (row.rank < 1 || row.rank > 3) continue
    picked[row.rank] = {
      rank: row.rank,
      rankLabel: podiumRankLabel(row.rank),
      nickname: row.nickname,
      avatarUrl: row.avatarUrl,
      prizeText: row.prizeText || null,
    }
  }
  const entries: DisplayPodiumEntry[] = []
  for (const rank of [2, 1, 3]) {
    const entry = picked[rank]
    if (entry) entries.push(entry)
  }
  return entries.length > 0 ? entries : null
}

export interface DisplayGrantView {
  id: string
  kind: DisplayGrantKind
  kindLabel: string
  issuedByName: string | null
  createdAt: string
  lastUsedAt: string | null
  lastUsedIp: string | null
  lastAppVersion: string | null
}

export interface DisplayCookieSpec {
  name: string
  path: string
  maxAge: number
}

export interface DisplayAppSession {
  origin: string
  token: string
  cookie: DisplayCookieSpec
}

export const displayAppLatestSchema = z.object({
  versionCode: z.number().int().positive(),
  versionName: z.string().min(1),
  minSdk: z.number().int().positive(),
  url: z.string().url(),
  sha256: z.string().regex(/^[0-9a-f]{64}$/),
})

export type DisplayAppLatest = z.infer<typeof displayAppLatestSchema>

export interface DisplayOpenOption {
  id: string
  label: string
}

export interface DisplayOpenGameOption extends DisplayOpenOption {
  status: GameStatus
  dealerStaffId: string | null
}

export interface DisplayOpenSetup {
  games: DisplayOpenGameOption[]
  templates: Array<DisplayOpenOption & { kind: 'tournament' | 'normal' }>
  dealers: DisplayOpenOption[]
}

/** 后台桌台大屏入口：当前地址、二维码与凭证 */
export interface TableDisplayResult {
  tableId: string
  tableName: string
  storeName: string
  token: string
  url: string
  qrDataUrl: string
  allowsLegacyDisplay: boolean
  /** 租户大屏是 HiPro 蓝皮肤时钟时才能编辑公告；否则 notice 为 null */
  notice: string | null
  grants: DisplayGrantView[]
  /** 能拼成 *.ifelt.cn 时给出旧版 display.txt；其它域名没有 */
  stick: DisplayStickFile | null
  /** 刚签发的 config.json，只在下载接口返回 */
  config: DisplayConfigFile | null
  /** 本桌挂在哪张主桌下；主桌自己为空 */
  hostTableId: string | null
  hostTableName: string | null
  subTables: Array<{ id: string; name: string }>
  candidates: DisplayCandidateTable[]
}
